import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/button';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type {
	EndOfPageHideConfirmModalEndOfPageRecHideStatusMutation as EndOfPageHideConfirmModalEndOfPageRecHideStatusMutation$data,
	EndOfPageHideConfirmModalEndOfPageRecHideStatusMutationVariables as EndOfPageHideConfirmModalEndOfPageRecHideStatusMutation$variables,
} from './__types__/EndOfPageHideConfirmModalEndOfPageRecHideStatusMutation.ts';
import { i18n } from './messages.ts';
import { EndOfPageRecommendationQuery } from './EndOfPageRecommendationQuery.graphql.ts';
const EndOfPageHideConfirmModalEndOfPageRecHideStatusMutation = graphql`
	mutation EndOfPageHideConfirmModalEndOfPageRecHideStatusMutation(
		$type: String!
		$id: ID!
		$status: Boolean!
	) {
		setRecommendedPagesStatus(
			input: { entityType: $type, entityId: $id, enableRecommendedPages: $status }
		) {
			success
			errors {
				message
			}
		}
	}
`;
type EndOfPageHideConfirmModalProps = {
	entityType: string;
	entityId: string;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	spaceKey: string;
};

export const EndOfPageHideConfirmModal: FC<EndOfPageHideConfirmModalProps> = ({
	entityId,
	entityType,
	isOpen,
	setIsOpen,
	spaceKey,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { Modal } = useChoreographerAssets();
	const [mutateHideStatus, { error }] = useMutationInterop<
		EndOfPageHideConfirmModalEndOfPageRecHideStatusMutation$data,
		EndOfPageHideConfirmModalEndOfPageRecHideStatusMutation$variables
	>(EndOfPageHideConfirmModalEndOfPageRecHideStatusMutation, {
		refetchQueries: [
			{
				query: EndOfPageRecommendationQuery,
				variables: {
					type: 'page',
					id: entityId,
					exp: 'end_of_page',
					key: spaceKey,
				},
			},
		],
	});
	const cancelModal = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'page',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'hideEOPModalCancel',
				attributes: {
					contentId: entityId,
				},
			},
		}).fire();
		setIsOpen(false);
	}, [createAnalyticsEvent, setIsOpen, entityId]);

	const confirmModal = useCallback(() => {
		void mutateHideStatus({
			variables: {
				type: entityType,
				id: entityId,
				status: false,
			},
		}).catch(() => {
			// This is literally just to prevent jest from failing tests on error
			// behavior due to apparent "uncaught promise rejection." We actually
			// handle mutation error by deconstructing it above in the mutation hook
			// above and rendering an error component below.
		});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'page',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'hideEOPModalConfirm',
				attributes: {
					contentId: entityId,
				},
			},
		}).fire();
		setIsOpen(false);
	}, [createAnalyticsEvent, mutateHideStatus, setIsOpen, entityId, entityType]);

	if (error) {
		return (
			<ErrorBoundary attribution={Attribution.SMARTS}>
				<ErrorDisplay error={error} />
			</ErrorBoundary>
		);
	}

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={cancelModal} testId="eop-hide-modal">
					<ModalHeader hasCloseButton>
						<ModalTitle>
							<FormattedMessage
								{...i18n.modal_title}
								values={{
									b: (boldWord: React.ReactNode[]) => <b>{boldWord}</b>,
								}}
							/>
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<FormattedMessage {...i18n.modal_info} />
						<br />
						<br />
						<FormattedMessage {...i18n.modal_info_show} />
					</ModalBody>
					<ModalFooter>
						<Button
							appearance="primary"
							onClick={confirmModal}
							testId="eop-hide-modal-confirm"
							autoFocus
						>
							<FormattedMessage {...i18n.modal_hide} />
						</Button>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};
